import { SerializedData, Store } from "bernie-core";
import { Logger, NOOP_LOGGER } from "bernie-logger";
import { action, observable } from "mobx";
import * as fetch from "isomorphic-fetch";
import { FAILED_SHORTLIST } from "config/systemEvents";
import { ShortlistResponse } from "typings/fetchResponseModels/ShortlistResponse";
import { safeResponseToJson } from "components/utility/FetchUtils";
import { HeartableEntity } from "components/shared/HeartButton/typings";

/**
 * The goal of this store is to fetch, save and unsave items from/to UCP through the client-side only.
 * This store will maintain a mapping of saved items which can be reusable across all components.
 * At the moment of client-side hydration (aka when javascript loads), it will fetch from UCP all the saved items and
 * store it inside as an array.
 */
export class ShortlistStore extends Store {
  private url: string;

  private clientToken: string;

  private clientId: string;

  @observable
  public savedFavorites: string[] = [];

  public constructor(
    state: SerializedData = {},
    logger: Logger = NOOP_LOGGER,
    url?: string,
    clientToken?: string,
    clientId?: string
  ) {
    super(state, logger);

    this.url = url || "";
    this.clientToken = clientToken || "";
    this.clientId = clientId || "";
  }

  // This will run once on client-side
  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
    this.setupShortlistedFavorites();
  }

  @action
  public setupShortlistedFavorites = () => {
    // Set up Promise to GET
    const fetchSavedFavoritesPromise = () =>
      fetch(this.generateUrl("fetch"), {
        method: "GET",
        headers: {
          ...this.generateHeaders(),
        },
        credentials: "include",
      });

    // Send GET and store them by id
    return Promise.resolve(fetchSavedFavoritesPromise())
      .then(safeResponseToJson)
      .then((body: ShortlistResponse) => {
        if (body && body.results) {
          const results = body.results[0];
          this.savedFavorites =
            (results && results.items && results.items.map((item: { itemId: string }) => item.itemId)) || [];
        }
      })
      .catch((err) => {
        this.logger.logEvent(FAILED_SHORTLIST, "Failed to retrieve saved items", err);
      });
  };

  public saveFavorite = (heartableEntity: HeartableEntity) => {
    if (!heartableEntity.id || !heartableEntity.entityType) {
      return Promise.resolve();
    }

    // Set up Promise to send a POST
    const saveFavoriteFetchPromise = () =>
      fetch(this.generateUrl("save", heartableEntity.id, heartableEntity.entityType), {
        method: "POST",
        headers: {
          ...this.generateHeaders(),
        },
        body: JSON.stringify(heartableEntity.payload),
        credentials: "include",
      });

    // Send POST
    return Promise.resolve(saveFavoriteFetchPromise())
      .then(safeResponseToJson)
      .catch((err) => {
        this.logger.logEvent(FAILED_SHORTLIST, `Failed to save item`, JSON.stringify(heartableEntity.payload), err);
      });
  };

  public unsaveFavorite = (heartableEntity: HeartableEntity) => {
    if (!heartableEntity.id || !heartableEntity.entityType) {
      return Promise.resolve();
    }

    // Set up Promise to send a POST
    const unsaveFavoriteFetchPromise = () =>
      fetch(this.generateUrl("remove", heartableEntity.id, heartableEntity.entityType), {
        method: "POST",
        headers: {
          ...this.generateHeaders(),
        },
        credentials: "include",
      });

    // Send POST
    return Promise.resolve(unsaveFavoriteFetchPromise()).catch((err) => {
      this.logger.logEvent(FAILED_SHORTLIST, "Failed to unsave item", err);
    });
  };

  private generateHeaders = () => ({
    Accept: "application/json",
    "Content-Type": "application/json",
    "client-token": this.clientToken,
  });

  private generateUrl = (type: string, id = "", configId = "") => {
    return configId === ""
      ? `${this.url}/${type}/${id}?clientId=${this.clientId}`
      : `${this.url}/${type}/${id}?clientId=${this.clientId}&configId=${configId}`;
  };
}
